import React, { useState, useRef, useEffect } from "react";
import {
  Building2,
  MapPin,
  Palette,
  ChevronDown,
  ChevronUp,
  Clock,
  ImagePlus,
  Check,
  CheckCircle,
  X,
  Upload,
  LayoutDashboard,
  FolderOpen,
  Users,
  FileText,
  DollarSign,
  Globe,
  Banknote,
  Lock,
  Scale,
} from "lucide-react";
import { formatPhoneNumber } from "../../../utils/formatPhone";
import SearchableSelect from "../../ui/SearchableSelect";

interface FirmProfileStepProps {
  data: {
    name: string;
    primaryEmail: string;
    primaryPhone: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      region: string;
      postal: string;
      country: string;
    };
    jurisdiction: { country: string; region: string };
    branding: {
      logoUrl: string | null;
      primaryColor: string;
      accentColor: string;
    };
    timezone: string;
    currency: string;
    numbering: {
      matterPrefix: string;
      invoicePrefix: string;
      receiptPrefix: string;
    };
    // Passed through from wizard state  -  not edited here
    practiceAreas: string[];
    firmType: "solicitor" | "litigation" | "full_service";
  };
  onChange: (data: FirmProfileStepProps["data"]) => void;
  onLogoFile?: (file: File | null) => void;
}

const COUNTRY_OPTIONS = [
  { value: "CA", label: "Canada" },
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
];

const TIMEZONE_OPTIONS = [
  { value: "America/Toronto", label: "Eastern (Toronto)", description: "Canada" },
  { value: "America/Winnipeg", label: "Central (Winnipeg)", description: "Canada" },
  { value: "America/Edmonton", label: "Mountain (Edmonton)", description: "Canada" },
  { value: "America/Vancouver", label: "Pacific (Vancouver)", description: "Canada" },
  { value: "America/Halifax", label: "Atlantic (Halifax)", description: "Canada" },
  { value: "America/St_Johns", label: "Newfoundland (St. John's)", description: "Canada" },
  { value: "America/New_York", label: "Eastern (New York)", description: "United States" },
  { value: "America/Chicago", label: "Central (Chicago)", description: "United States" },
  { value: "America/Denver", label: "Mountain (Denver)", description: "United States" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)", description: "United States" },
  { value: "Europe/London", label: "London (GMT)", description: "Other" },
  { value: "Australia/Sydney", label: "Sydney (AEST)", description: "Other" },
];

const CURRENCY_OPTIONS = [
  { value: "CAD", label: "CAD  -  Canadian Dollar" },
  { value: "USD", label: "USD  -  US Dollar" },
  { value: "GBP", label: "GBP  -  British Pound" },
  { value: "AUD", label: "AUD  -  Australian Dollar" },
  { value: "EUR", label: "EUR  -  Euro" },
];

const inputClasses =
  "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm";

const labelClasses = "block text-sm font-medium text-slate-700 mb-1.5";

const sectionHeaderClasses = "text-base font-semibold text-slate-800 mb-3";

const PRIMARY_COLOR_PRESETS = [
  { value: "#0F5132", label: "Deep Emerald" },
  { value: "#1E3A5F", label: "Navy Blue" },
  { value: "#0C4A6E", label: "Ocean Blue" },
  { value: "#4A2545", label: "Deep Plum" },
  { value: "#8B2500", label: "Burgundy" },
  { value: "#7F1D1D", label: "Dark Red" },
  { value: "#2D3436", label: "Charcoal" },
  { value: "#1A1A2E", label: "Midnight" },
  { value: "#713F12", label: "Warm Brown" },
  { value: "#3B0764", label: "Deep Violet" },
  { value: "#134E4A", label: "Dark Teal" },
  { value: "#1E1B4B", label: "Indigo" },
];

const ACCENT_COLOR_PRESETS = [
  { value: "#198754", label: "Emerald" },
  { value: "#2563EB", label: "Blue" },
  { value: "#7C3AED", label: "Purple" },
  { value: "#DC2626", label: "Red" },
  { value: "#D97706", label: "Amber" },
  { value: "#0891B2", label: "Cyan" },
  { value: "#65A30D", label: "Lime" },
  { value: "#EC4899", label: "Pink" },
  { value: "#F59E0B", label: "Gold" },
  { value: "#6366F1", label: "Indigo" },
  { value: "#14B8A6", label: "Teal" },
];

const PROVINCES = [
  { code: "ON", name: "Ontario" },
  { code: "BC", name: "British Columbia" },
  { code: "AB", name: "Alberta" },
  { code: "QC", name: "Quebec" },
  { code: "MB", name: "Manitoba" },
  { code: "SK", name: "Saskatchewan" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "YT", name: "Yukon" },
];

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/svg+xml", "image/webp"];

const FirmProfileStep: React.FC<FirmProfileStepProps> = ({ data, onChange, onLogoFile }) => {
  const [addressOpen, setAddressOpen] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(data.branding.logoUrl || null);
  const [dragOver, setDragOver] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Sync local preview when parent data changes (e.g., returning to this step)
  useEffect(() => {
    if (data.branding.logoUrl && data.branding.logoUrl !== logoPreview) {
      setLogoPreview(data.branding.logoUrl);
    }
  }, [data.branding.logoUrl]);

  const handleLogoFile = (file: File) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) return;
    if (logoPreview && logoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }
    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    onLogoFile?.(file);
    // Store in parent state so it survives step navigation
    onChange({ ...data, branding: { ...data.branding, logoUrl: url } });
  };

  const handleLogoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      handleLogoFile(file);
    }
  };

  const handleLogoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoFile(file);
    }
  };

  const removeLogo = () => {
    if (logoPreview && logoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(null);
    onLogoFile?.(null);
    onChange({ ...data, branding: { ...data.branding, logoUrl: null } });
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  // Helper to lighten a hex color for hover/active states
  const lightenColor = (hex: string, amount: number): string => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.min(255, (num >> 16) + amount);
    const g = Math.min(255, ((num >> 8) & 0x00ff) + amount);
    const b = Math.min(255, (num & 0x0000ff) + amount);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  };

  // Deep-clone data and set a value at a dot-separated path
  const update = (path: string, value: string) => {
    const clone = JSON.parse(JSON.stringify(data));
    const keys = path.split(".");
    let obj: any = clone;
    for (let i = 0; i < keys.length - 1; i++) {
      obj = obj[keys[i]];
    }
    obj[keys[keys.length - 1]] = value;
    onChange(clone);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          Set up your firm profile
        </h2>
        <p className="text-slate-500 mt-1 text-sm">
          Tell us about your practice so we can customize Kavoa for you.
        </p>
      </div>

      {/* Section 0: Jurisdiction */}
      {data.jurisdiction.country === "CA" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-1">
            <Scale className="h-5 w-5 text-brand" />
            <h3 className={sectionHeaderClasses}>Jurisdiction</h3>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            Select the province or territory where your firm primarily operates.
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {PROVINCES.map((p) => {
              const isSelected = data.jurisdiction.region === p.code;
              return (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => update("jurisdiction.region", p.code)}
                  className={`relative rounded-xl p-3.5 text-left cursor-pointer transition-all duration-200 border-2 ${
                    isSelected
                      ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-500/20 shadow-sm scale-[1.02]"
                      : "border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30 hover:shadow-sm hover:scale-[1.01]"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`text-[11px] font-bold w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200 ${
                        isSelected
                          ? "bg-emerald-600 text-white shadow-sm"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {p.code}
                    </span>
                    <span
                      className={`text-[13px] font-medium leading-tight ${
                        isSelected ? "text-emerald-800" : "text-slate-700"
                      }`}
                    >
                      {p.name}
                    </span>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-slate-400 text-center">
            Operating in multiple provinces? You can update this later in Settings.
          </p>
        </div>
      )}

      {/* Section 0b: US State Jurisdiction */}
      {data.jurisdiction.country === "US" && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-1">
            <Scale className="h-5 w-5 text-brand" />
            <h3 className={sectionHeaderClasses}>Jurisdiction</h3>
          </div>
          <p className="text-sm text-slate-500 mb-5">
            Select the state where your firm primarily operates.
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {US_STATES.map((s) => {
              const isSelected = data.jurisdiction.region === s.code;
              return (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => update("jurisdiction.region", s.code)}
                  className={`relative rounded-xl p-3.5 text-left cursor-pointer transition-all duration-200 border-2 ${
                    isSelected
                      ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-500/20 shadow-sm scale-[1.02]"
                      : "border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30 hover:shadow-sm hover:scale-[1.01]"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className={`text-[11px] font-bold w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200 ${
                        isSelected
                          ? "bg-emerald-600 text-white shadow-sm"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {s.code}
                    </span>
                    <span
                      className={`text-[13px] font-medium leading-tight ${
                        isSelected ? "text-emerald-800" : "text-slate-700"
                      }`}
                    >
                      {s.name}
                    </span>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-slate-400 text-center">
            Operating in multiple states? You can update this later in Settings.
          </p>
        </div>
      )}

      {/* Section 1: Basic Information */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-5 w-5 text-brand" />
          <h3 className={sectionHeaderClasses}>Basic Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClasses}>
              Firm Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              className={inputClasses}
              value={data.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="e.g. Smith & Associates LLP"
            />
          </div>
          <div>
            <label className={labelClasses}>
              Primary Email
              <Lock className="inline h-3 w-3 ml-1 text-slate-400" />
            </label>
            <input
              type="email"
              className={inputClasses + " bg-slate-50 text-slate-500 cursor-not-allowed"}
              value={data.primaryEmail}
              readOnly
              tabIndex={-1}
            />
            <p className="text-xs text-slate-400 mt-1">
              You can change this in Settings later.
            </p>
          </div>
          <div>
            <label className={labelClasses}>Primary Phone</label>
            <input
              type="tel"
              className={inputClasses}
              value={data.primaryPhone}
              onChange={(e) => update("primaryPhone", e.target.value)}
              onBlur={() => {
                if (data.primaryPhone) {
                  update("primaryPhone", formatPhoneNumber(data.primaryPhone));
                }
              }}
              placeholder="(416) 555-0100"
            />
          </div>
          {/* Empty cell for alignment */}
          <div className="hidden md:block" />
        </div>
      </div>

      {/* Section 2: Address (collapsible) */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <button
          type="button"
          className="flex items-center justify-between w-full"
          onClick={() => setAddressOpen(!addressOpen)}
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-brand" />
            <h3 className={sectionHeaderClasses}>Address</h3>
          </div>
          {addressOpen ? (
            <ChevronUp className="h-5 w-5 text-slate-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-slate-400" />
          )}
        </button>

        {addressOpen && (
          <div className="mt-4 space-y-4">
            <div>
              <label className={labelClasses}>Street Address</label>
              <input
                type="text"
                className={inputClasses}
                value={data.address.line1}
                onChange={(e) => update("address.line1", e.target.value)}
                placeholder="123 Bay Street"
              />
            </div>
            <div>
              <label className={labelClasses}>Suite / Unit</label>
              <input
                type="text"
                className={inputClasses}
                value={data.address.line2}
                onChange={(e) => update("address.line2", e.target.value)}
                placeholder="Suite 400"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClasses}>City</label>
                <input
                  type="text"
                  className={inputClasses}
                  value={data.address.city}
                  onChange={(e) => update("address.city", e.target.value)}
                  placeholder="Toronto"
                />
              </div>
              <div>
                <label className={labelClasses}>
                  {data.jurisdiction.country === "US" ? "State" : "Province"}
                </label>
                <input
                  type="text"
                  className={inputClasses}
                  value={data.address.region}
                  onChange={(e) => update("address.region", e.target.value)}
                  placeholder="Ontario"
                />
              </div>
              <div>
                <label className={labelClasses}>
                  {(data.jurisdiction.country === "US" || data.address.country === "US") ? "ZIP Code" : "Postal Code"}
                </label>
                <input
                  type="text"
                  className={inputClasses}
                  value={data.address.postal}
                  onChange={(e) => update("address.postal", e.target.value)}
                  placeholder={(data.jurisdiction.country === "US" || data.address.country === "US") ? "10001" : "M5H 2N2"}
                />
              </div>
            </div>
            <div>
              <label className={labelClasses}>Country</label>
              <SearchableSelect
                options={COUNTRY_OPTIONS}
                value={data.address.country || null}
                onChange={(val) => update("address.country", val || "")}
                placeholder="Select country"
                icon={Globe}
              />
            </div>
          </div>
        )}
      </div>

      {/* Section 3: Preferences */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-brand" />
          <h3 className={sectionHeaderClasses}>Preferences</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className={labelClasses}>Timezone</label>
            <SearchableSelect
              options={TIMEZONE_OPTIONS}
              value={data.timezone || null}
              onChange={(val) => update("timezone", val || "")}
              placeholder="Select timezone"
              icon={Clock}
              searchPlaceholder="Search timezones..."
            />
          </div>
          <div>
            <label className={labelClasses}>Currency</label>
            <SearchableSelect
              options={CURRENCY_OPTIONS}
              value={data.currency || null}
              onChange={(val) => update("currency", val || "")}
              placeholder="Select currency"
              icon={Banknote}
            />
          </div>
        </div>
      </div>

      {/* Section 4: Branding */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="h-5 w-5 text-brand" />
          <h3 className={sectionHeaderClasses}>Branding</h3>
        </div>
        <p className="text-sm text-slate-500 -mt-2 mb-6">
          Customize how your firm looks across Kavoa. Choose colors and upload your logo.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-8">
          {/* Left column: Logo + Color pickers */}
          <div className="space-y-8">
            {/* Logo Upload */}
            <div>
              <label className={labelClasses}>Firm Logo</label>
              <input
                ref={logoInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.svg,.webp"
                className="hidden"
                onChange={handleLogoInputChange}
              />

              {logoPreview ? (
                <div className="relative group rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-center gap-5">
                  <div className="h-20 w-20 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                    <img
                      src={logoPreview}
                      alt="Firm logo preview"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700">Logo uploaded</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Click below to change or remove
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        Replace
                      </button>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleLogoDrop}
                  onClick={() => logoInputRef.current?.click()}
                  className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all duration-200 p-8 flex flex-col items-center justify-center text-center ${
                    dragOver
                      ? "border-brand-accent bg-brand-light/50 scale-[1.01]"
                      : "border-slate-300 bg-slate-50/50 hover:border-brand-accent hover:bg-brand-light/30"
                  }`}
                >
                  <div
                    className={`h-12 w-12 rounded-xl flex items-center justify-center mb-3 transition-colors ${
                      dragOver ? "bg-brand-light text-brand-accent" : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    <ImagePlus className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-medium text-slate-700">
                    {dragOver ? "Drop your logo here" : "Upload your firm logo"}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Drag & drop or click to browse. JPG, PNG, SVG, or WebP.
                  </p>
                </div>
              )}
            </div>

            {/* Primary Color */}
            <div>
              <label className={labelClasses}>Primary Color</label>
              <p className="text-xs text-slate-400 mb-3">
                Used for your sidebar background, headers, and buttons.
              </p>
              <div className="flex flex-wrap gap-2.5 mb-4">
                {PRIMARY_COLOR_PRESETS.map((preset) => {
                  const isSelected =
                    (data.branding.primaryColor || "").toUpperCase() ===
                    preset.value.toUpperCase();
                  return (
                    <button
                      key={preset.value}
                      type="button"
                      title={preset.label}
                      onClick={() => update("branding.primaryColor", preset.value)}
                      className={`relative h-10 w-10 rounded-full transition-all duration-200 flex items-center justify-center shrink-0 ${
                        isSelected
                          ? "ring-2 ring-offset-2 ring-emerald-500 scale-110 shadow-md"
                          : "hover:scale-110 hover:shadow-md ring-1 ring-slate-200"
                      }`}
                      style={{ backgroundColor: preset.value }}
                    >
                      {isSelected && (
                        <Check className="h-4 w-4 text-white drop-shadow-sm" />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="color"
                    value={data.branding.primaryColor || "#0F5132"}
                    onChange={(e) =>
                      update("branding.primaryColor", e.target.value.toUpperCase())
                    }
                    className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer appearance-none bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none"
                  />
                </div>
                <input
                  type="text"
                  className={inputClasses + " flex-1 font-mono"}
                  value={data.branding.primaryColor}
                  onChange={(e) =>
                    update("branding.primaryColor", e.target.value)
                  }
                  placeholder="#0F5132"
                  maxLength={7}
                />
              </div>
            </div>

            {/* Accent Color */}
            <div>
              <label className={labelClasses}>Accent Color</label>
              <p className="text-xs text-slate-400 mb-3">
                Used for highlights, active states, and interactive elements.
              </p>
              <div className="flex flex-wrap gap-2.5 mb-4">
                {ACCENT_COLOR_PRESETS.map((preset) => {
                  const isSelected =
                    (data.branding.accentColor || "").toUpperCase() ===
                    preset.value.toUpperCase();
                  return (
                    <button
                      key={preset.value}
                      type="button"
                      title={preset.label}
                      onClick={() => update("branding.accentColor", preset.value)}
                      className={`relative h-10 w-10 rounded-full transition-all duration-200 flex items-center justify-center shrink-0 ${
                        isSelected
                          ? "ring-2 ring-offset-2 ring-emerald-500 scale-110 shadow-md"
                          : "hover:scale-110 hover:shadow-md ring-1 ring-slate-200"
                      }`}
                      style={{ backgroundColor: preset.value }}
                    >
                      {isSelected && (
                        <Check className="h-4 w-4 text-white drop-shadow-sm" />
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="color"
                    value={data.branding.accentColor || "#198754"}
                    onChange={(e) =>
                      update("branding.accentColor", e.target.value.toUpperCase())
                    }
                    className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer appearance-none bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none"
                  />
                </div>
                <input
                  type="text"
                  className={inputClasses + " flex-1 font-mono"}
                  value={data.branding.accentColor}
                  onChange={(e) =>
                    update("branding.accentColor", e.target.value)
                  }
                  placeholder="#198754"
                  maxLength={7}
                />
              </div>
            </div>
          </div>

          {/* Right column: Live Preview */}
          <div className="hidden lg:block">
            <label className={labelClasses}>Live Preview</label>
            <div className="sticky top-4">
              <div
                className="rounded-2xl overflow-hidden shadow-lg border border-slate-200"
                style={{ width: 220 }}
              >
                {/* Sidebar header */}
                <div
                  className="px-4 py-4 flex items-center gap-2.5"
                  style={{
                    backgroundColor: data.branding.primaryColor || "#0F5132",
                  }}
                >
                  {logoPreview ? (
                    <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center overflow-hidden shrink-0">
                      <img
                        src={logoPreview}
                        alt="Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="h-8 w-8 rounded-lg bg-white/20 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-white truncate">
                      {data.name || "Your Firm"}
                    </div>
                    <div className="text-[10px] text-white/60 truncate">
                      Legal Practice
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div
                  className="h-px"
                  style={{
                    backgroundColor: lightenColor(
                      data.branding.primaryColor || "#0F5132",
                      20
                    ),
                  }}
                />

                {/* Nav items */}
                <div
                  className="py-2 space-y-0.5 px-2"
                  style={{
                    backgroundColor: data.branding.primaryColor || "#0F5132",
                  }}
                >
                  {[
                    { icon: LayoutDashboard, label: "Dashboard", active: true },
                    { icon: FolderOpen, label: "Matters", active: false },
                    { icon: Users, label: "Clients", active: false },
                    { icon: FileText, label: "Documents", active: false },
                    { icon: DollarSign, label: "Billing", active: false },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors"
                      style={{
                        backgroundColor: item.active
                          ? "rgba(255,255,255,0.15)"
                          : "transparent",
                      }}
                    >
                      <item.icon
                        className="h-3.5 w-3.5 shrink-0"
                        style={{
                          color: item.active
                            ? data.branding.accentColor || "#198754"
                            : "rgba(255,255,255,0.5)",
                        }}
                      />
                      <span
                        className="text-[11px] font-medium"
                        style={{
                          color: item.active
                            ? "#ffffff"
                            : "rgba(255,255,255,0.6)",
                        }}
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Spacer */}
                <div
                  className="h-8"
                  style={{
                    backgroundColor: data.branding.primaryColor || "#0F5132",
                  }}
                />

                {/* User section */}
                <div
                  className="px-4 py-3 flex items-center gap-2.5 border-t"
                  style={{
                    backgroundColor: data.branding.primaryColor || "#0F5132",
                    borderColor: lightenColor(
                      data.branding.primaryColor || "#0F5132",
                      20
                    ),
                  }}
                >
                  <div
                    className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                    style={{
                      backgroundColor: data.branding.accentColor || "#198754",
                    }}
                  >
                    JD
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-medium text-white truncate">
                      Jane Doe
                    </div>
                    <div className="text-[9px] text-white/50 truncate">
                      Administrator
                    </div>
                  </div>
                </div>
              </div>

              {/* Accent color bar preview */}
              <div className="mt-4 rounded-xl overflow-hidden shadow-sm border border-slate-200">
                <div
                  className="h-2"
                  style={{
                    backgroundColor: data.branding.accentColor || "#198754",
                  }}
                />
                <div className="bg-white px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-slate-500">
                      Accent preview
                    </span>
                    <div
                      className="px-2 py-0.5 rounded-full text-[9px] font-semibold text-white"
                      style={{
                        backgroundColor:
                          data.branding.accentColor || "#198754",
                      }}
                    >
                      Active
                    </div>
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <div
                      className="h-1.5 rounded-full flex-1"
                      style={{
                        backgroundColor:
                          data.branding.accentColor || "#198754",
                      }}
                    />
                    <div
                      className="h-1.5 rounded-full flex-1 opacity-40"
                      style={{
                        backgroundColor:
                          data.branding.accentColor || "#198754",
                      }}
                    />
                    <div
                      className="h-1.5 rounded-full flex-1 opacity-20"
                      style={{
                        backgroundColor:
                          data.branding.accentColor || "#198754",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmProfileStep;
